import React from "react";
import { useTranslation } from "react-i18next";
import {
  BadgeCheck,
  Ban,
  CircleDollarSign,
  CirclePause,
  Star,
  Users,
} from "lucide-react";
import { MetricCard, MetricCardsGrid } from "../shared";
import { SERVICES_CHART_CARD_ICON_COLOR } from "./constants";
import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";
import type { ServicePopularityMetrics } from "./serviceDetailTypes";

interface ServicePopularitySectionProps {
  metrics: ServicePopularityMetrics;
}

const STATUS_ICONS = {
  done: BadgeCheck,
  canceled: Ban,
  onHold: CirclePause,
} as const;

function formatRevenue(value: number): string {
  return formatAnalyticsCurrency(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

export const ServicePopularitySection: React.FC<
  ServicePopularitySectionProps
> = ({ metrics }) => {
  const { t } = useTranslation();

  return (
    <section className="flex w-full flex-col gap-4 rounded-2xl border border-[#E5E5E5] bg-white p-6">
      <div className="flex items-center gap-3">
        <Star
          className="shrink-0"
          style={{
            width: 13.33,
            height: 12.68,
            color: SERVICES_CHART_CARD_ICON_COLOR,
          }}
          strokeWidth={2}
          aria-hidden
        />
        <h2 className="text-sm font-semibold text-[#171717]">
          {t("analytics.services.detail.popularity", "Popularity")}
        </h2>
      </div>
      <div className="flex w-full flex-col gap-3">
        <MetricCardsGrid columns={2}>
          <MetricCard
            title={t("analytics.services.detail.totalRevenue", "Total Revenue")}
            value={formatRevenue(metrics.totalRevenue)}
            icon={<CircleDollarSign />}
          />
          <MetricCard
            title={t("analytics.services.detail.guestUsed", "Guest used")}
            value={
              metrics.guestsUsedPercent == null
                ? t("analytics.common.notAvailable", "N/A")
                : `${metrics.guestsUsedPercent.toFixed(1)}%`
            }
            icon={<Users />}
          />
        </MetricCardsGrid>
        <MetricCardsGrid columns={4}>
          {metrics.statusCounts.map((status) => {
            const Icon = STATUS_ICONS[status.key];

            return (
              <MetricCard
                key={status.key}
                title={t(
                  `analytics.services.detail.status.${status.key}`,
                  status.label,
                )}
                value={String(status.count)}
                icon={<Icon />}
              />
            );
          })}
        </MetricCardsGrid>
      </div>
    </section>
  );
};
